import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ChipsHelper from '@/Utility/ChipsHelper';
import TypeConverter from '@/Utility/TypeConverter';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import TextBoxViewModel from '@/ViewModels/Controls/TextBoxViewModel';
import ImageViewModel from '@/ViewModels/Controls/ImageViewModel';
import Utilities from '@/Utility/Utilities';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';


export default class ChipsViewModel<T extends (string | number)> extends ControlViewModel {

    /**
     * Ein Observable, was angibt, ob das Chips Control fertig geladen ist
     * Wird im ChipsControl gesetzt, wenn das jQuery Element geladen ist
     * Kann z.B. im Filter verwendet werden, um zu pr�fen, ob die Chips geladen sind (H�henberechnung)
     */
    public isComponentReady: KnockoutObservable<boolean>;

    /**
     * Ein Observable das semicolon-separierte-strings enth�lt z.B.: Chips1;Chips2;Chips3' ODER ein Array
     */
    public originValues: KnockoutObservable<string | T[]>;

    /**
     * Ein computed-observable welches ein array von chips enth�lt das aus den originValues berechnet wird.
     * z.B.: ['Chips1', 'Chips2', 'Chips3']
     */
    public values: KnockoutComputed<T[]>;

    /**
     * Der vom Benutzer eingegebene Wert.
     * Wird gebunden auf das Input-Html-Element.
     */
    public value: KnockoutObservable<string>;

    public imageRemoveVM: ImageViewModel;

    public activeChips: KnockoutObservable<T[]>;
    public tooltips: KnockoutObservable<any>;
    public inputVisible: KnockoutObservable<boolean>;
    public sortAlphabetically: KnockoutObservable<boolean>;
    public addNewValue: Function;
    public onClick: Function;
    private dotNetTypeName: string;

    private textBoxViewModel: TextBoxViewModel | NumericTextBoxViewModel;
    private textBoxUiGenerator: string;    

    public constructor(protected params: any) {
        super(params);
        this.isComponentReady = ko.observable(false);
        this.originValues = this.observableOrDefault(params.values, '');
        this.values = this.definePureComputed({
            read: this.readValues.bind(this, this.originValues),
            write: this.writeValues.bind(this, this.originValues)
        });
        this.value = this.observableOrDefault('');
        this.onClick = this.handlerOrDefault(params.onClick, () => { });
        this.activeChips = this.observableOrDefault([]);
        this.addNewValue = this.handlerOrDefault(params.addNewValue, this.addNewValueInternal.bind(this));
        this.tooltips = this.observableOrDefault({});
        this.inputVisible = this.observableOrDefault(true);
        this.sortAlphabetically = this.observableOrDefault(params.sortAlphabetically, false);
        this.dotNetTypeName = this.paramOrDefault(params.dotNetTypeName, null);
        this.textBoxViewModel = this.createTextBoxViewModel();
        this.textBoxUiGenerator = this.isNumericChip() ? 'NumericTextBox' : 'TextBox';
        this.imageRemoveVM = this.createImageRemoveVM();
    }

    private createImageRemoveVM(): ImageViewModel {
        return new ImageViewModel({
            source: 'close_filter.svg'
        })
    }

    private createTextBoxViewModel(): TextBoxViewModel | NumericTextBoxViewModel {
        const params = {
            value: this.value,
            isEnabled: this.isEnabled,
            isVisible: this.inputVisible,
            parentLocator: this.parentLocator,
            locator: this.locator
        };

        if (TypeConverter.typeIsArray(this.dotNetTypeName))
        {        
            const arrayType = TypeConverter.getArrayType(this.dotNetTypeName);
            if (this.isNumericChip()) {
                const info = NumericTypeHelper.getDotNetTypeInfo(arrayType)
                return new NumericTextBoxViewModel($.extend(params, {
                    min: info.min,
                    max: info.max
                }));
            }
            else if (TypeConverter.typeIsChar(arrayType)) {
                return new TextBoxViewModel($.extend(params, {
                    maxLength: 1
                }));
            }
        }

        return new TextBoxViewModel(params);
    }

    /**
     * Ein Handler f�r das Best�tigen eines neuen Wertes
     * @param keyCode
     */
    public onKeyUp(keyCode: number): void {
        if (keyCode === 13) {
            this.addNewValue();
        }
    }

    /**
     * Ein Handler f�r das Entfernen eines Wertes
     * @param keyCode
     */
    public onRemove(value: T): void {
        const values = this.readAllValues(this.originValues);
        const index = values.indexOf(value);

        if (index > -1) {
            values.splice(index, 1);
        }

        this.values(values);
    }

    public onAdd(value: T): void {
        var values = this.readAllValues(this.originValues);
        if (values.indexOf(value) === -1) {
            values.splice(0, 0, value);
            this.values(values);
        }

        this.textBoxViewModel.resetValue();
    }

    /**
     * Ein Handler f�r das automatische Hinzuf�gen eines neuen Wertes
     */
    public onBlur(): void {
        this.addNewValue();
    }

    public resetValue(): void {
        this.value('');
        this.values([]);
    }

    public getValueFromID(id: string): string {
        return id;
    }

    public showRemove(id: string): boolean {
        return this.isEnabled();
    }

    private addNewValueInternal(): void {
        let value = this.value();
        if (!value) {
            return;
        }

        if (Utilities.isString(value)) {
            value = value.trim();
        }

        const safeValue = this.getTypeSafeValue<T>(value);

        this.onAdd(safeValue);
    }

    protected getTypeSafeValue<T>(value: string): T {
        let safeValue: T;
        if (this.isNumericChip())
            safeValue = parseFloat(value) as any;
        else
            safeValue = value as any;

        return safeValue;
    }

    protected readValues(observable): T[] {
        const value = observable();

        if (Array.isArray(value))
            return [...value]; // Kopie des Arrays herausgeben

        if (!this.sortAlphabetically())
            return ChipsHelper.getValues<T>(value);
        else
            return ChipsHelper.getValuesAlphabetically<T>(value);
    }

    protected readAllValues(observable): T[] {
        return this.readValues(observable);
    }

    private writeValues(observable: KnockoutObservable<string | T[]>, value: T[]) {
        this.setActiveChips(this.activeChips(), value);

        if (TypeConverter.typeIsArray(this.dotNetTypeName)) {
            observable(value);
            return;
        }

        // Es handelt sich um einen String, der durch ; separiert wird.
        // Bei diesem m�ssen die Werte maskiert werden
        const stringValues = value as string[];   
        for (var i = 0; i < stringValues.length; i++) {
            stringValues[i] = ChipsHelper.maskValue(value[i]);
        }

        var joined = stringValues.join(';');
        observable(joined);
    }

    /**
     * Setzt die aktiven Chips f�r die es einen Filter gibt
     * @param activeChips die Chips, die aktiv sein sollen
     * @param value die existierenden Filter f�r die Chips
     */
    protected setActiveChips(activeChips: T[], value: T[]): void {
        const newActiveChips = _.intersection(activeChips, value);
        // Wenn kein aktiver Chip entfernt wird, sollen die aktiven Chips nicht �berschrieben werden
        // Ansonsten w�rden Nebenefekte auftreten, wie z.B. das Zur�cksetzen von transienten Filtern
        if (this.activeChips().length !== newActiveChips.length) {
            this.activeChips(newActiveChips);
        }
    }

    private isNumericChip(): boolean {
        return TypeConverter.typeIsNumericArray(this.dotNetTypeName);
    }
}

ComponentManager.setViewModel('ChipsViewModel', ChipsViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');